import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { LuPencil } from "react-icons/lu";
import { CiSearch } from "react-icons/ci";
import { IoClose } from "react-icons/io5";
import { MdAdminPanelSettings } from "react-icons/md";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import Skeleton from "../../components/Loader/Skeleton";
import { useImpersonation } from "../../components/context/ImpersonationContext";

/* ── sub-components ─────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const active = (status || "").toLowerCase() === "active";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", fontSize: 12, fontWeight: 600,
      borderRadius: 20, whiteSpace: "nowrap",
      backgroundColor: active ? "#ECFDF5" : "#F9FAFB",
      border: `1px solid ${active ? "#6EE7B7" : "#E5E7EB"}`,
      color: active ? "#065F46" : "#6B7280",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
        backgroundColor: active ? "#10B981" : "#9CA3AF", display: "inline-block",
      }} />
      {status || "—"}
    </span>
  );
};

const IndustryBadge = ({ name }) => {
  if (!name) return <span style={{ color: "#D1D5DB", fontSize: 13 }}>—</span>;
  const palette = [
    ["#EEF2FF", "#4F46E5"], ["#FFF7ED", "#C2410C"], ["#F0FDF4", "#15803D"],
    ["#FDF4FF", "#9333EA"], ["#FFF1F4", "#C9194A"],
  ];
  const idx = name.charCodeAt(0) % palette.length;
  const [bg, color] = palette[idx];
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", fontSize: 12, fontWeight: 600,
      borderRadius: 20, background: bg, color, whiteSpace: "nowrap",
    }}>
      {name}
    </span>
  );
};

const RowSkeleton = () => (
  <tr>
    {[40, 170, 130, 90, 90, 150].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px" }}>
        <Skeleton width={`${w}px`} />
      </td>
    ))}
  </tr>
);

const paginationBtn = (active, disabled) => ({
  display: "inline-flex", alignItems: "center", gap: 4,
  height: 32, minWidth: 32, padding: "0 10px", borderRadius: 7,
  border: active ? "1px solid #111827" : "1px solid #E5E7EB",
  background: active ? "#111827" : "#fff",
  color: active ? "#fff" : "#374151",
  fontWeight: active ? 600 : 400, fontSize: 13,
  cursor: disabled ? "default" : "pointer",
  opacity: disabled && !active ? 0.45 : 1,
  pointerEvents: disabled ? "none" : "auto",
  transition: "all 0.12s",
});

/* ── main component ─────────────────────────────────────────────── */
const Vendors = () => {
  const navigate = useNavigate();
  const { impersonate } = useImpersonation();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [impersonatingId, setImpersonatingId] = useState(null);

  useEffect(() => {
    const t = setTimeout(fetchVendors, 400);
    return () => clearTimeout(t);
  }, [searchQuery, page, limit, selectedFilter]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      let url = `admin_vendor_list?page=${page}&limit=${limit}`;
      if (selectedFilter === "Active") url += `&status=1`;
      else if (selectedFilter === "Inactive") url += `&status=0`;
      if (searchQuery.trim()) url += `&name=${encodeURIComponent(searchQuery.trim())}`;

      const res = await apicall.get(url);
      if (res && res.data) {
        setVendors(res.data);
        setTotalCount(res.pagination?.total || 0);
      } else {
        setVendors([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Failed to fetch vendors:", err);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAsVendor = async (vendor) => {
    const active = (vendor.status || "").toLowerCase() === "active";
    if (!active) return;
    setImpersonatingId(vendor.user_id);
    try {
      await impersonate(vendor.user_id, [vendor.first_name, vendor.last_name].filter(Boolean).join(" "));
    } catch (err) {
      toast.error(err.message || "Failed to open vendor view");
      setImpersonatingId(null);
    }
  };

  const handleEditVendor = (user_id) => navigate(`/vendors/edit/${user_id}`);

  /* pagination */
  const totalPages = Math.ceil(totalCount / limit);
  const getPageNumbers = () => {
    const nums = [], show = 5, half = Math.floor(show / 2);
    if (totalPages <= show) {
      for (let i = 1; i <= totalPages; i++) nums.push(i);
    } else {
      nums.push(1);
      let s = Math.max(2, page - half), e = Math.min(totalPages - 1, page + half);
      if (page <= half + 1) e = show - 1;
      else if (page >= totalPages - half) s = totalPages - show + 2;
      if (s > 2) nums.push("...");
      for (let i = s; i <= e; i++) nums.push(i);
      if (e < totalPages - 1) nums.push("...");
      nums.push(totalPages);
    }
    return nums;
  };

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", background: "#F8FAFC" }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h4 style={{ fontWeight: 700, color: "#111827", marginBottom: 4, fontSize: 22 }}>
            Vendor Accounts
          </h4>
          <p style={{ color: "#6B7280", fontSize: 13, margin: 0 }}>
            {loading ? "Loading…" : `${totalCount} vendor${totalCount !== 1 ? "s" : ""} registered`}
          </p>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB",
        padding: "16px 20px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}>
        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 260px", minWidth: 200 }}>
          <CiSearch style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: "#9CA3AF", fontSize: 18, pointerEvents: "none",
          }} />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            onKeyDown={(e) => e.key === "Enter" && fetchVendors()}
            style={{
              width: "100%", height: 38, paddingLeft: 36,
              paddingRight: searchQuery ? 34 : 12,
              border: "1px solid #E5E7EB", borderRadius: 8,
              fontSize: 13, color: "#111827", outline: "none", background: "#F9FAFB",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#C9194A")}
            onBlur={(e) => (e.target.style.borderColor = "#E5E7EB")}
          />
          {searchQuery && (
            <IoClose
              onClick={() => { setSearchQuery(""); setPage(1); }}
              style={{
                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                color: "#9CA3AF", cursor: "pointer", fontSize: 18,
              }}
            />
          )}
        </div>

        {/* Status filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#6B7280", fontWeight: 500, whiteSpace: "nowrap" }}>
            Status:
          </span>
          <Form.Select
            value={selectedFilter}
            onChange={(e) => { setSelectedFilter(e.target.value); setPage(1); }}
            style={{
              height: 38, fontSize: 13, borderRadius: 8,
              border: "1px solid #E5E7EB", color: "#111827",
              background: "#F9FAFB", minWidth: 130, cursor: "pointer",
            }}
          >
            <option value="All">All Vendors</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Form.Select>
        </div>

        {(selectedFilter !== "All" || searchQuery) && (
          <button
            onClick={() => { setSearchQuery(""); setSelectedFilter("All"); setPage(1); }}
            style={{
              background: "none", border: "1px solid #E5E7EB",
              borderRadius: 8, padding: "6px 12px", fontSize: 13,
              color: "#6B7280", cursor: "pointer", whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#C9194A"; e.currentTarget.style.color = "#C9194A"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#6B7280"; }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 12,
        border: "1px solid #E5E7EB", overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%", borderCollapse: "collapse",
            fontSize: 13, minWidth: 680,
          }}>
            <thead>
              <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E5E7EB" }}>
                {["#", "Vendor Name", "Industry", "Users", "Status", "Actions"].map((h, i) => (
                  <th key={h} style={{
                    padding: "12px 16px",
                    textAlign: i === 0 || i === 3 ? "center" : "left",
                    fontWeight: 600, color: "#374151", fontSize: 12,
                    textTransform: "uppercase", letterSpacing: "0.5px",
                    whiteSpace: "nowrap",
                    borderRight: i < 5 ? "1px solid #F3F4F6" : "none",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(6).fill(null).map((_, i) => <RowSkeleton key={i} />)
              ) : vendors.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "52px 0", color: "#9CA3AF" }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
                    <div style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>No vendors found</div>
                    <div style={{ fontSize: 12 }}>Try adjusting your search or filter</div>
                  </td>
                </tr>
              ) : (
                vendors.map((vendor, idx) => {
                  const isActive = (vendor.status || "").toLowerCase() === "active";
                  const isLoading = impersonatingId === vendor.user_id;
                  const fullName = [vendor.first_name, vendor.last_name].filter(Boolean).join(" ");

                  return (
                    <tr
                      key={vendor.user_id}
                      style={{ borderBottom: "1px solid #F3F4F6", transition: "background 0.1s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAFA")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {/* # */}
                      <td style={{ padding: "14px 16px", textAlign: "center", color: "#9CA3AF", fontWeight: 500, borderRight: "1px solid #F3F4F6" }}>
                        {(page - 1) * limit + idx + 1}
                      </td>

                      {/* Vendor Name */}
                      <td style={{ padding: "14px 16px", borderRight: "1px solid #F3F4F6" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                            background: "linear-gradient(135deg, #C9194A 0%, #7C3AED 100%)",
                            color: "#fff", display: "flex", alignItems: "center",
                            justifyContent: "center", fontWeight: 700, fontSize: 13,
                          }}>
                            {(fullName || "?").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: "#111827", lineHeight: 1.3 }}>
                              {fullName || "—"}
                            </div>
                            <div style={{ color: "#9CA3AF", fontSize: 11 }}>
                              {vendor.email || vendor.phone_number || `ID #${vendor.user_id}`}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Industry */}
                      <td style={{ padding: "14px 16px", borderRight: "1px solid #F3F4F6" }}>
                        <IndustryBadge name={vendor.industry_name} />
                      </td>

                      {/* Users (crew count) */}
                      <td style={{ padding: "14px 16px", textAlign: "center", borderRight: "1px solid #F3F4F6" }}>
                        <span style={{
                          background: "#EEF2FF", color: "#4F46E5",
                          borderRadius: 20, padding: "2px 10px",
                          fontSize: 12, fontWeight: 600,
                        }}>
                          {vendor.user_count ?? 0}
                        </span>
                      </td>

                      {/* Status */}
                      <td style={{ padding: "14px 16px", borderRight: "1px solid #F3F4F6" }}>
                        <StatusBadge status={vendor.status} />
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {/* Edit */}
                          <button
                            onClick={() => handleEditVendor(vendor.user_id)}
                            title="Edit vendor profile"
                            style={{
                              display: "flex", alignItems: "center", gap: 5,
                              background: "#F9FAFB", border: "1px solid #E5E7EB",
                              borderRadius: 7, padding: "6px 12px",
                              fontSize: 12, fontWeight: 600, color: "#374151",
                              cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; e.currentTarget.style.borderColor = "#D1D5DB"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "#F9FAFB"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
                          >
                            <LuPencil size={12} /> Edit
                          </button>

                          {/* View as Vendor */}
                          <button
                            onClick={() => handleViewAsVendor(vendor)}
                            disabled={!isActive || isLoading}
                            title={!isActive ? "Vendor must be active to view" : "View portal as this vendor"}
                            style={{
                              display: "flex", alignItems: "center", gap: 5,
                              background: isActive ? "#FFF1F4" : "#F9FAFB",
                              border: `1px solid ${isActive ? "#FECDD3" : "#E5E7EB"}`,
                              borderRadius: 7, padding: "6px 12px",
                              fontSize: 12, fontWeight: 600,
                              color: isActive ? "#C9194A" : "#9CA3AF",
                              cursor: isActive ? "pointer" : "not-allowed",
                              opacity: isActive ? 1 : 0.5,
                              whiteSpace: "nowrap", minWidth: 115,
                              justifyContent: "center", transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) => { if (!isActive) return; e.currentTarget.style.background = "#FFE4E9"; e.currentTarget.style.borderColor = "#F9A8B8"; }}
                            onMouseLeave={(e) => { if (!isActive) return; e.currentTarget.style.background = "#FFF1F4"; e.currentTarget.style.borderColor = "#FECDD3"; }}
                          >
                            {isLoading ? (
                              <>
                                <span style={{
                                  width: 10, height: 10, borderRadius: "50%",
                                  border: "2px solid #C9194A", borderTopColor: "transparent",
                                  display: "inline-block", animation: "spin 0.7s linear infinite",
                                }} />
                                Opening…
                              </>
                            ) : (
                              <><MdAdminPanelSettings size={13} /> View as Vendor</>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ──────────────────────────────────────── */}
        {!loading && totalPages > 0 && (
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "14px 20px", borderTop: "1px solid #F3F4F6", flexWrap: "wrap", gap: 12,
          }}>
            <span style={{ fontSize: 13, color: "#6B7280" }}>
              Showing{" "}
              <strong style={{ color: "#111827" }}>
                {Math.min((page - 1) * limit + 1, totalCount)}–{Math.min(page * limit, totalCount)}
              </strong>{" "}
              of <strong style={{ color: "#111827" }}>{totalCount}</strong> vendors
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1} style={paginationBtn(false, page === 1)}>
                <FaArrowLeft size={11} /> Prev
              </button>
              {getPageNumbers().map((num, i) => (
                <button
                  key={i}
                  onClick={() => num !== "..." && setPage(num)}
                  disabled={num === "..."}
                  style={paginationBtn(num === page, num === "...")}
                >
                  {num}
                </button>
              ))}
              <button onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} style={paginationBtn(false, page === totalPages)}>
                Next <FaArrowRight size={11} />
              </button>
              <select
                value={limit}
                onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
                style={{
                  height: 34, fontSize: 13, borderRadius: 7,
                  border: "1px solid #E5E7EB", padding: "0 8px",
                  color: "#374151", background: "#F9FAFB", cursor: "pointer", marginLeft: 4,
                }}
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Vendors;
